import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { DatePicker } from '@mui/x-date-pickers';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';

import { TgDuration } from '@playq/services-shared';
import { AccessLevel } from '@playq/octopus2-auth';

import { ItemText, SelectFormControl } from '/shared/SelectFormControl';
import { useStateChange } from '/common/useStateChange';
import { clipboardWrite, confirmAboutUnsavedChanges, formatDate } from '/helpers';
import { snackbarService } from '/common/snackbarService';
import { CircularProgressStyled } from '/shared/Spinner';

import { ExpirationLabels, IApiKeyFormProps, ICurrentAccessLevelOption, ISelectAccessLevelOptions } from './types';
import { EllipsisTextField, FormContainer } from './styles';
import {
  expirationOptions,
  expirationPresets,
  getInitialExpirationState,
  makeTgDuration,
  selectAccessLevelOptions,
} from './helpers';

const today = DateTime.now();
const tommorow = today.plus({ days: 1 });

export const ApiKeyForm: FC<IApiKeyFormProps> = (props) => {
  const {
    isKeyCreationBeingProcessed,
    isKeyRegenerationBeingProcessed,
    tableQueryToPerformAfterKeyCreation,
    defaultKeyAccessLevel,
    keyFormSubmissionHandler,
    keyCreationDialogTogglingHandler,
    keyDataToCopy,
    mode = 'create',
    initialValue,
    handleCloseRegenerateDialog,
  }: IApiKeyFormProps = props;

  const regenerateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getInitialKeyAccessLevel = useCallback(() => {
    const levelToFind = initialValue?.accessLevel ?? defaultKeyAccessLevel;

    const targetEntry = Object.entries(AccessLevel).find(([, value]) => value === levelToFind);
    if (!targetEntry) {
      return { label: 'Read' as keyof ISelectAccessLevelOptions, value: AccessLevel.Read };
    }
    const [label, value] = targetEntry as [keyof ISelectAccessLevelOptions, AccessLevel];

    return { label, value };
  }, [defaultKeyAccessLevel, initialValue]);

  const targetApiKey = keyDataToCopy?.key;
  const isTargetApiKeyAvailable = Boolean(targetApiKey);
  const isCreateMode = mode === 'create';

  const [isRegenerateApproved, setIsRegenerateApproved] = useState<boolean>(false);
  const [isKeyCopied, setIsKeyCopied] = useState<boolean>(false);
  const [keyName, setKeyName] = useState<string>(initialValue?.name || '');
  const [keyAccessLevel, setKeyAccessLevel] = useState<ICurrentAccessLevelOption>(() => getInitialKeyAccessLevel());
  const [formError, setFormError] = useState<string>('');
  const [expirationState, setExpirationState] = useState<{
    mode: ExpirationLabels;
    duration?: TgDuration;
    customDate?: DateTime;
  }>(() => getInitialExpirationState(initialValue));

  useEffect(() => {
    return () => {
      if (regenerateTimerRef.current) {
        clearTimeout(regenerateTimerRef.current);
      }
    };
  }, []);

  const notApprovedRegeneration = !isCreateMode && !isRegenerateApproved;

  const buttonTitle = useMemo(() => {
    if (isTargetApiKeyAvailable) return 'Copy  key';
    const regenerationLabel = notApprovedRegeneration ? 'Pre-confirm Regenerate' : 'Regenerate key';

    return !isCreateMode ? regenerationLabel : 'Generate key';
  }, [isCreateMode, isTargetApiKeyAvailable, notApprovedRegeneration]);

  const noChanges = useMemo(() => {
    if (mode === 'regenerate') return false;
    return keyName === '' && _.isEqual(keyAccessLevel, getInitialKeyAccessLevel());
  }, [keyName, keyAccessLevel, getInitialKeyAccessLevel, mode]);

  useStateChange(noChanges);

  const areThereAnyFormErrors = useMemo((): boolean => Boolean(formError), [formError]);

  const handleAccessLevelChange = (event: SelectChangeEvent<unknown>) => {
    const { value } = event.target;
    const targetAccessLevelLabel = value as keyof ISelectAccessLevelOptions;
    const targetAccessLevelValue: AccessLevel = selectAccessLevelOptions[targetAccessLevelLabel];

    setKeyAccessLevel({
      label: targetAccessLevelLabel,
      value: targetAccessLevelValue,
    });
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newKeyName: string = event.target.value;

    setKeyName(newKeyName);

    if (newKeyName.length < 3) {
      setFormError('The field must be a string with a minimum length of 3 characters');
    } else if (areThereAnyFormErrors) {
      setFormError('');
    }
  };

  const isSaveDisabled = useMemo(
    () =>
      keyName === '' ||
      noChanges ||
      areThereAnyFormErrors ||
      isKeyCreationBeingProcessed ||
      isKeyRegenerationBeingProcessed,
    [keyName, noChanges, areThereAnyFormErrors, isKeyCreationBeingProcessed, isKeyRegenerationBeingProcessed]
  );

  const handleSave = () => {
    keyFormSubmissionHandler({
      keyName,
      keyAccessLevel: keyAccessLevel.value,
      tableQuery: tableQueryToPerformAfterKeyCreation,
      ttl: expirationState.duration,
      isCreateMode: mode === 'create',
      id: initialValue?.id,
    });
  };

  const handleCopyClick = () => {
    if (!targetApiKey) {
      return;
    }
    clipboardWrite(targetApiKey);
    setIsKeyCopied(true);
    snackbarService.success(`Key ${keyDataToCopy?.name} successfully copied to clipboard`);
  };

  const handleActionButtonClick = () => {
    if (!isRegenerateApproved && mode === 'regenerate') {
      setIsRegenerateApproved(true);

      if (regenerateTimerRef.current) {
        clearTimeout(regenerateTimerRef.current);
      }

      regenerateTimerRef.current = setTimeout(() => {
        setIsRegenerateApproved(false);
      }, 10000);
      return;
    }
    if (isTargetApiKeyAvailable) {
      handleCopyClick();
    } else if (!isSaveDisabled) {
      if (regenerateTimerRef.current) {
        clearTimeout(regenerateTimerRef.current);
      }
      handleSave();
    }
  };

  const handleExpirationSelect = (event: SelectChangeEvent<unknown>) => {
    const labelTarget = event.target.value as ExpirationLabels;
    const preset = expirationPresets.find((p) => p.label === labelTarget);

    if (!preset) return;

    if (preset.days === undefined) {
      setExpirationState({
        mode: ExpirationLabels.noExpiration,
        duration: undefined,
      });
      return;
    }

    if (preset.days === 0) {
      setExpirationState({
        mode: ExpirationLabels.custom,
        duration: undefined,
        customDate: today.plus({ days: 1 }),
      });
      return;
    }

    setExpirationState({
      mode: preset.label,
      duration: makeTgDuration(preset.days),
    });
  };

  const onDateChange = (newValue: DateTime<true> | null) => {
    if (!newValue) return;
    const dt = newValue;

    const diff = Math.ceil(newValue.diff(today, 'days').days);

    setExpirationState({
      mode: ExpirationLabels.custom,
      duration: makeTgDuration(diff),
      customDate: dt,
    });
  };

  const handleCloseAction = () => {
    if (!isTargetApiKeyAvailable) {
      keyCreationDialogTogglingHandler(false);
      handleCloseRegenerateDialog?.();
      return;
    }

    if (isKeyCopied) {
      keyCreationDialogTogglingHandler(false);
      handleCloseRegenerateDialog?.();
      return;
    }

    if (isTargetApiKeyAvailable && !isKeyCopied) {
      confirmAboutUnsavedChanges(
        () => setIsKeyCopied(true),
        false,
        'Please make sure you have saved your API key before closing creating dialog'
      );
    }
  };

  return (
    <Stack direction='column' spacing={2}>
      <FormControl fullWidth error={areThereAnyFormErrors}>
        <TextField
          disabled={isTargetApiKeyAvailable || !isCreateMode}
          value={keyName}
          error={areThereAnyFormErrors}
          onChange={handleNameChange}
          size='small'
          autoFocus
          fullWidth
          required
          variant='outlined'
          margin='normal'
          name='apiKeyName'
          label='Name'
          autoComplete='off'
        />

        {areThereAnyFormErrors && <FormHelperText>{formError}</FormHelperText>}
      </FormControl>

      <FormContainer $hasCustom={expirationState.mode === ExpirationLabels.custom}>
        <SelectFormControl
          disabled={isTargetApiKeyAvailable || !isCreateMode}
          label='Access'
          selectedOption={keyAccessLevel.label}
          variant='outlined'
          options={Object.keys(selectAccessLevelOptions)}
          onOptionSelect={handleAccessLevelChange}
          selectProps={{ size: 'small' }}
        />

        <SelectFormControl
          disabled={isTargetApiKeyAvailable}
          label={isCreateMode ? 'Expiration' : 'New Expiration'}
          selectedOption={expirationState.mode}
          variant='outlined'
          options={expirationOptions.map((opt) => opt.value)}
          customOptionsMapper={(optionValue: string) => {
            const option = expirationOptions.find((opt) => opt.value === optionValue);

            return (
              <ItemText key={optionValue} value={optionValue}>
                {option?.label || optionValue}
              </ItemText>
            );
          }}
          onOptionSelect={handleExpirationSelect}
          selectProps={{ size: 'small' }}
        />

        {expirationState.mode === ExpirationLabels.custom && (
          <DatePicker
            label='Expiration date'
            value={expirationState.customDate ?? tommorow}
            disabled={isTargetApiKeyAvailable}
            onChange={onDateChange}
            minDate={tommorow}
            slotProps={{ textField: { size: 'small' } }}
            sx={{ width: 250 }}
          />
        )}
      </FormContainer>

      <Divider flexItem />

      {mode === 'regenerate' && !isTargetApiKeyAvailable && (
        <>
          <Alert severity='info'>
            {initialValue?.ttl && initialValue?.expiration ? (
              <>
                The current key <b>expires</b> on <b>{formatDate(initialValue.expiration)}</b>
                <br />
                The <b>TTL</b> was previously set to{' '}
                <b>
                  {initialValue.ttl.length} {initialValue.ttl.unit.toLowerCase()}
                </b>
              </>
            ) : (
              <>
                The current key has <b>no expiration</b> date
              </>
            )}
          </Alert>
          <Alert severity='warning'>Regenerating this key will invalidate the previous secret.</Alert>
        </>
      )}

      <EllipsisTextField
        disabled={!isTargetApiKeyAvailable}
        value={targetApiKey}
        fullWidth
        autoComplete='off'
        variant='outlined'
        color='success'
        size={isTargetApiKeyAvailable ? 'medium' : 'small'}
        focused={isTargetApiKeyAvailable}
        margin='normal'
        name='targetApiKey'
        label={
          isTargetApiKeyAvailable
            ? 'Write it down and Keep it safe'
            : 'Your new API Key will be shown here after saving'
        }
        InputLabelProps={{
          shrink: Boolean(targetApiKey),
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton disabled={!isTargetApiKeyAvailable} onClick={handleCopyClick} edge='end'>
                <ContentCopyIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Stack
        sx={{ mt: ({ spacing }) => `${spacing(3)} !important`, pb: ({ spacing }) => spacing(1) }}
        direction='row'
        justifyContent='space-between'
      >
        <Button variant='outlined' color='secondary' type='button' size='large' onClick={handleCloseAction}>
          Close
        </Button>

        <Button
          disabled={!isTargetApiKeyAvailable && isSaveDisabled}
          variant={notApprovedRegeneration ? 'outlined' : 'contained'}
          color={notApprovedRegeneration ? 'warning' : 'primary'}
          type='button'
          size='large'
          onClick={handleActionButtonClick}
          sx={{ minWidth: '215px' }}
        >
          {isKeyCreationBeingProcessed || isKeyRegenerationBeingProcessed ? (
            <CircularProgressStyled size='20px' />
          ) : (
            buttonTitle
          )}
        </Button>
      </Stack>
    </Stack>
  );
};
